/**
 * Recipe Handler — centered modal + self-contained HTML download.
 *
 * Backend wire: { t: 'show_recipe', recipe: { title, ingredients[], steps[], servings?, notes? } }
 * Triggered sync-to-speech when the agent calls send_recipe.
 */

import { resolvePartnerId, resolvePartnerLogoUrl } from './partnerLogo.js';

const STYLES_ID = 'ttp-recipe-handler-styles';

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function slugify(title) {
  const slug = String(title ?? 'recipe')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'recipe';
}

function normalizeRecipe(input) {
  if (!input || typeof input !== 'object') return null;
  const recipe = input.recipe && typeof input.recipe === 'object' ? input.recipe : input;
  const title = String(recipe.title ?? '').trim();
  const ingredients = Array.isArray(recipe.ingredients)
    ? recipe.ingredients.map((x) => String(x).trim()).filter(Boolean)
    : [];
  const steps = Array.isArray(recipe.steps)
    ? recipe.steps.map((x) => String(x).trim()).filter(Boolean)
    : [];
  if (!title || (ingredients.length === 0 && steps.length === 0)) {
    console.warn('[RecipeHandler] Invalid recipe payload — need title plus ingredients or steps', input);
    return null;
  }
  return {
    title,
    ingredients,
    steps,
    servings: recipe.servings ? String(recipe.servings).trim() : '',
    notes: recipe.notes ? String(recipe.notes).trim() : '',
  };
}

/** Shared download-page CSS (self-contained, no external assets). */
const RECIPE_PAGE_CSS = `
  :root {
    --bg: #f6f3ee;
    --paper: #fffcf8;
    --ink: #1c1917;
    --muted: #78716c;
    --accent: #2f6b52;
    --accent-soft: #e8f2ec;
    --accent-deep: #1e4d3a;
    --warm: #c4713a;
    --warm-soft: #fdf3eb;
    --line: rgba(28, 25, 23, 0.08);
    --shadow: 0 24px 60px rgba(28, 25, 23, 0.12);
    --radius: 20px;
  }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    min-height: 100vh;
    font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
    color: var(--ink);
    background:
      radial-gradient(circle at top right, rgba(47, 107, 82, 0.08), transparent 28rem),
      radial-gradient(circle at bottom left, rgba(196, 113, 58, 0.08), transparent 24rem),
      var(--bg);
    line-height: 1.55;
    padding: 32px 16px 48px;
  }
  .sheet {
    max-width: 760px;
    margin: 0 auto;
    background: var(--paper);
    border-radius: calc(var(--radius) + 4px);
    box-shadow: var(--shadow);
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.7);
  }
  .hero {
    position: relative;
    padding: 36px 32px 28px;
    background:
      linear-gradient(135deg, var(--accent-deep) 0%, var(--accent) 55%, #3d8b67 100%);
    color: #fff;
    overflow: hidden;
  }
  .hero::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 85% 15%, rgba(255,255,255,0.16), transparent 42%),
      radial-gradient(circle at 10% 100%, rgba(255,255,255,0.08), transparent 35%);
    pointer-events: none;
  }
  .hero-inner { position: relative; z-index: 1; }
  .hero-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
  }
  .hero-head .eyebrow { margin-bottom: 0; }
  .partner-logo-wrap {
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    padding: 8px 12px;
    max-width: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .partner-logo {
    height: 28px;
    width: auto;
    max-width: 120px;
    object-fit: contain;
    display: block;
  }
  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    opacity: 0.88;
    margin-bottom: 12px;
  }
  .eyebrow-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #ffd166;
    box-shadow: 0 0 0 4px rgba(255, 209, 102, 0.22);
  }
  h1 {
    margin: 0;
    font-family: "Iowan Old Style", "Palatino Linotype", "Book Antiqua", Georgia, serif;
    font-size: clamp(2rem, 5vw, 2.75rem);
    line-height: 1.08;
    font-weight: 600;
    letter-spacing: -0.02em;
  }
  .servings {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 8px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px);
    font-size: 14px;
    font-weight: 500;
  }
  .content { padding: 28px 28px 32px; }
  .grid {
    display: grid;
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
    gap: 24px;
  }
  .section { min-width: 0; }
  .section-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 14px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent-deep);
  }
  .section-title::after {
    content: "";
    flex: 1;
    height: 1px;
    background: linear-gradient(to right, var(--line), transparent);
  }
  .ingredients-box {
    margin: 0;
    padding: 0;
    list-style: none;
    background: var(--accent-soft);
    border: 1px solid rgba(47, 107, 82, 0.12);
    border-radius: 16px;
    overflow: hidden;
  }
  .ingredients-box li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(47, 107, 82, 0.08);
    font-size: 15px;
  }
  .ingredients-box li:last-child { border-bottom: 0; }
  .ingredients-box li::before {
    content: "";
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    margin-top: 0.45em;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 0 3px rgba(47, 107, 82, 0.14);
  }
  .steps { margin: 0; padding: 0; list-style: none; display: grid; gap: 12px; }
  .step {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr);
    gap: 14px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid var(--line);
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(28, 25, 23, 0.04);
  }
  .step-num {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: linear-gradient(180deg, var(--warm-soft), #fff);
    border: 1px solid rgba(196, 113, 58, 0.18);
    color: var(--warm);
    font-size: 14px;
    font-weight: 800;
  }
  .step-text { font-size: 15px; color: #292524; padding-top: 6px; }
  .notes {
    margin-top: 24px;
    padding: 16px 18px 16px 20px;
    border-radius: 16px;
    background: var(--warm-soft);
    border: 1px solid rgba(196, 113, 58, 0.16);
    color: #57534e;
    font-size: 14px;
  }
  .notes strong {
    display: block;
    margin-bottom: 6px;
    color: #9a3412;
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .footer {
    margin-top: 28px;
    padding-top: 18px;
    border-top: 1px solid var(--line);
    text-align: center;
    font-size: 12px;
    color: var(--muted);
    letter-spacing: 0.04em;
  }
  @media (max-width: 720px) {
    .hero { padding: 28px 22px 22px; }
    .content { padding: 22px 18px 26px; }
    .grid { grid-template-columns: 1fr; gap: 22px; }
  }
  @media print {
    body { background: #fff; padding: 0; }
    .sheet { box-shadow: none; border: none; max-width: none; }
  }
`;

function buildIngredientsHtml(ingredients) {
  if (!ingredients.length) return '';
  const items = ingredients.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  return `
    <section class="section">
      <h2 class="section-title">Ingredients</h2>
      <ul class="ingredients-box">${items}</ul>
    </section>`;
}

function buildStepsHtml(steps) {
  if (!steps.length) return '';
  const items = steps.map((step, i) => `
    <li class="step">
      <div class="step-num">${i + 1}</div>
      <div class="step-text">${escapeHtml(step)}</div>
    </li>`).join('');
  return `
    <section class="section">
      <h2 class="section-title">Method</h2>
      <ol class="steps">${items}</ol>
    </section>`;
}

function buildNotesHtml(notes) {
  if (!notes) return '';
  return `<div class="notes"><strong>Chef's tip</strong>${escapeHtml(notes)}</div>`;
}

function buildPartnerLogoHtml(logoUrl) {
  if (!logoUrl) return '';
  return `<div class="partner-logo-wrap"><img class="partner-logo" src="${escapeHtml(logoUrl)}" alt=""></div>`;
}

function buildHeroHeadHtml(logoUrl) {
  const logo = buildPartnerLogoHtml(logoUrl);
  const eyebrow = '<div class="eyebrow"><span class="eyebrow-dot"></span> Recipe card</div>';
  if (!logo) return eyebrow;
  return `<div class="hero-head">${eyebrow}${logo}</div>`;
}

export function buildRecipeHtml(recipe, logoUrl = null) {
  const servings = recipe.servings
    ? `<div class="servings"><span aria-hidden="true">🍽</span> ${escapeHtml(recipe.servings)}</div>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(recipe.title)}</title>
  <style>${RECIPE_PAGE_CSS}</style>
</head>
<body>
  <article class="sheet">
    <header class="hero">
      <div class="hero-inner">
        ${buildHeroHeadHtml(logoUrl)}
        <h1>${escapeHtml(recipe.title)}</h1>
        ${servings}
      </div>
    </header>
    <div class="content">
      <div class="grid">
        ${buildIngredientsHtml(recipe.ingredients)}
        ${buildStepsHtml(recipe.steps)}
      </div>
      ${buildNotesHtml(recipe.notes)}
      <p class="footer">Saved from TalkToPC · Your voice shopping assistant</p>
    </div>
  </article>
</body>
</html>`;
}

function downloadRecipeHtml(recipe, logoUrl = null) {
  try {
    const html = buildRecipeHtml(recipe, logoUrl);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugify(recipe.title)}.html`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    console.log('[RecipeHandler] Downloaded recipe HTML:', a.download);
  } catch (e) {
    console.warn('[RecipeHandler] HTML download failed:', e);
  }
}

function injectStyles() {
  if (document.getElementById(STYLES_ID)) return;
  const s = document.createElement('style');
  s.id = STYLES_ID;
  s.textContent = `
.ttp-rh-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483640;
  background: rgba(15, 23, 20, 0);
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  transition: background 0.32s ease, backdrop-filter 0.32s ease;
  pointer-events: none;
}
.ttp-rh-backdrop.ttp-rh-visible {
  background: rgba(15, 23, 20, 0.52);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  pointer-events: all;
}
.ttp-rh-card {
  position: relative;
  width: min(94vw, 680px);
  max-height: calc(100vh - 32px);
  background: #fffcf8;
  border-radius: 24px;
  box-shadow: 0 28px 80px rgba(15, 23, 20, 0.22), 0 8px 24px rgba(15, 23, 20, 0.08);
  display: flex;
  flex-direction: column;
  opacity: 0;
  transform: scale(0.94) translateY(12px);
  transition: opacity 0.32s cubic-bezier(0.23, 1, 0.32, 1),
              transform 0.32s cubic-bezier(0.23, 1, 0.32, 1);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.65);
}
.ttp-rh-backdrop.ttp-rh-visible .ttp-rh-card {
  opacity: 1;
  transform: scale(1) translateY(0);
}
.ttp-rh-hero {
  position: relative;
  padding: 28px 56px 22px 24px;
  background: linear-gradient(135deg, #1e4d3a 0%, #2f6b52 55%, #3d8b67 100%);
  color: #fff;
  flex-shrink: 0;
}
.ttp-rh-hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 85% 15%, rgba(255,255,255,0.16), transparent 42%),
    radial-gradient(circle at 10% 100%, rgba(255,255,255,0.08), transparent 35%);
  pointer-events: none;
}
.ttp-rh-hero-inner { position: relative; z-index: 1; }
.ttp-rh-hero-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding-right: 36px;
}
.ttp-rh-hero-head .ttp-rh-eyebrow { margin-bottom: 0; }
.ttp-rh-partner-logo-wrap {
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 10px;
  padding: 6px 10px;
  max-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ttp-rh-partner-logo {
  height: 26px;
  width: auto;
  max-width: 100px;
  object-fit: contain;
  display: block;
}
.ttp-rh-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font: 700 11px/1 "Segoe UI", system-ui, sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.88;
  margin-bottom: 10px;
}
.ttp-rh-eyebrow-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ffd166;
  box-shadow: 0 0 0 4px rgba(255, 209, 102, 0.22);
}
.ttp-rh-title {
  font-family: "Iowan Old Style", "Palatino Linotype", "Book Antiqua", Georgia, serif;
  font-size: clamp(1.45rem, 4vw, 1.95rem);
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.02em;
}
.ttp-rh-servings {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 7px 13px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  font: 500 13px/1.2 "Segoe UI", system-ui, sans-serif;
}
.ttp-rh-hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}
.ttp-rh-hero-actions .ttp-rh-btn {
  flex: 0 1 auto;
  padding: 10px 16px;
  border: none;
  border-radius: 12px;
  font: 600 13px/1 "Segoe UI", system-ui, sans-serif;
  cursor: pointer;
  transition: transform 0.12s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.ttp-rh-hero-actions .ttp-rh-btn:active { transform: scale(0.98); }
.ttp-rh-hero-actions .ttp-rh-btn-secondary {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.28);
}
.ttp-rh-hero-actions .ttp-rh-btn-secondary:hover { background: rgba(255, 255, 255, 0.22); }
.ttp-rh-hero-actions .ttp-rh-btn-primary {
  background: #fff;
  color: #1e4d3a;
  box-shadow: 0 8px 20px rgba(15, 23, 20, 0.18);
}
.ttp-rh-hero-actions .ttp-rh-btn-primary:hover { background: #f4faf7; }
.ttp-rh-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
  backdrop-filter: blur(8px);
}
.ttp-rh-close:hover { background: rgba(255, 255, 255, 0.2); }
.ttp-rh-body {
  padding: 20px 22px 8px;
  overflow-y: auto;
  flex: 1;
  background: linear-gradient(180deg, #fffcf8 0%, #f8f5f0 100%);
}
.ttp-rh-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  gap: 18px;
}
.ttp-rh-section { min-width: 0; margin-bottom: 0; }
.ttp-rh-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
  font: 800 11px/1 "Segoe UI", system-ui, sans-serif;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1e4d3a;
}
.ttp-rh-section-title::after {
  content: "";
  flex: 1;
  height: 1px;
  background: linear-gradient(to right, rgba(28,25,23,0.08), transparent);
}
.ttp-rh-ingredients {
  margin: 0;
  padding: 0;
  list-style: none;
  background: #e8f2ec;
  border: 1px solid rgba(47, 107, 82, 0.12);
  border-radius: 14px;
  overflow: hidden;
}
.ttp-rh-ingredients li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(47, 107, 82, 0.08);
  font: 14px/1.45 "Segoe UI", system-ui, sans-serif;
  color: #292524;
}
.ttp-rh-ingredients li:last-child { border-bottom: 0; }
.ttp-rh-ingredients li::before {
  content: "";
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  margin-top: 0.45em;
  border-radius: 50%;
  background: #2f6b52;
  box-shadow: 0 0 0 3px rgba(47, 107, 82, 0.14);
}
.ttp-rh-steps {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;
}
.ttp-rh-step {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid rgba(28, 25, 23, 0.08);
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(28, 25, 23, 0.04);
}
.ttp-rh-step-num {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: linear-gradient(180deg, #fdf3eb, #fff);
  border: 1px solid rgba(196, 113, 58, 0.18);
  color: #c4713a;
  font: 800 13px/1 "Segoe UI", system-ui, sans-serif;
}
.ttp-rh-step-text {
  font: 14px/1.45 "Segoe UI", system-ui, sans-serif;
  color: #292524;
  padding-top: 5px;
}
.ttp-rh-notes-wrap { margin-top: 16px; }
.ttp-rh-notes {
  padding: 14px 16px;
  border-radius: 14px;
  background: #fdf3eb;
  border: 1px solid rgba(196, 113, 58, 0.16);
  font: 13px/1.5 "Segoe UI", system-ui, sans-serif;
  color: #57534e;
}
.ttp-rh-notes-label {
  display: block;
  margin-bottom: 6px;
  font: 800 11px/1 "Segoe UI", system-ui, sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9a3412;
}
@media (max-width: 640px) {
  .ttp-rh-backdrop { padding: 10px; }
  .ttp-rh-card { width: calc(100vw - 20px); border-radius: 20px; }
  .ttp-rh-grid { grid-template-columns: 1fr; }
  .ttp-rh-hero { padding: 24px 52px 22px 20px; }
  .ttp-rh-hero-actions { margin-top: 14px; }
  .ttp-rh-hero-actions .ttp-rh-btn { flex: 1 1 calc(50% - 5px); min-width: 120px; }
}
`;
  document.head.appendChild(s);
}

function _appendSectionTitle(parent, label) {
  const title = document.createElement('h3');
  title.className = 'ttp-rh-section-title';
  title.textContent = label;
  parent.appendChild(title);
}

export function createRecipeHandler(widget) {
  let backdrop = null;
  let _dismissed = false;
  let _savedHostZIndex = null;
  let _printFrame = null;
  let _lastLogoUrl = null;
  let _openedAsReshow = false;

  function _minimizeWidget() {
    try {
      const host = widget?.shadowRoot?.host;
      if (host) {
        _savedHostZIndex = host.style.zIndex || null;
        host.style.zIndex = String(2147483641);
      }
    } catch (e) { /* best-effort */ }
  }

  function _restoreWidget() {
    try {
      const host = widget?.shadowRoot?.host;
      if (host) {
        host.style.zIndex = _savedHostZIndex || '';
        _savedHostZIndex = null;
      }
    } catch (e) { /* best-effort */ }
  }

  function _teardownFrame() {
    if (_printFrame?.parentNode) {
      _printFrame.parentNode.removeChild(_printFrame);
    }
    _printFrame = null;
  }

  function _appendHeroHead(heroInner, logoUrl) {
    const eyebrow = document.createElement('div');
    eyebrow.className = 'ttp-rh-eyebrow';
    eyebrow.innerHTML = '<span class="ttp-rh-eyebrow-dot"></span> Recipe card';

    if (!logoUrl) {
      heroInner.appendChild(eyebrow);
      return;
    }

    const head = document.createElement('div');
    head.className = 'ttp-rh-hero-head';
    head.appendChild(eyebrow);

    const logoWrap = document.createElement('div');
    logoWrap.className = 'ttp-rh-partner-logo-wrap';
    const logoImg = document.createElement('img');
    logoImg.className = 'ttp-rh-partner-logo';
    logoImg.src = logoUrl;
    logoImg.alt = '';
    logoWrap.appendChild(logoImg);
    head.appendChild(logoWrap);

    heroInner.appendChild(head);
  }

  function _buildCard(recipe, logoUrl = null) {
    const card = document.createElement('div');
    card.className = 'ttp-rh-card';
    card.addEventListener('click', (e) => e.stopPropagation());

    const closeBtn = document.createElement('button');
    closeBtn.className = 'ttp-rh-close';
    closeBtn.setAttribute('aria-label', 'Close recipe');
    closeBtn.textContent = '\u00d7';
    closeBtn.addEventListener('click', dismissRecipe);

    const hero = document.createElement('header');
    hero.className = 'ttp-rh-hero';
    const heroInner = document.createElement('div');
    heroInner.className = 'ttp-rh-hero-inner';

    _appendHeroHead(heroInner, logoUrl);

    const titleEl = document.createElement('div');
    titleEl.className = 'ttp-rh-title';
    titleEl.textContent = recipe.title;
    heroInner.appendChild(titleEl);

    if (recipe.servings) {
      const servEl = document.createElement('div');
      servEl.className = 'ttp-rh-servings';
      servEl.innerHTML = `<span aria-hidden="true">\u{1F37D}</span> ${recipe.servings}`;
      heroInner.appendChild(servEl);
    }

    const actions = document.createElement('div');
    actions.className = 'ttp-rh-hero-actions';

    const saveBtn = document.createElement('button');
    saveBtn.className = 'ttp-rh-btn ttp-rh-btn-secondary';
    saveBtn.textContent = 'Save again';
    saveBtn.addEventListener('click', () => downloadRecipeHtml(recipe, logoUrl));

    const printBtn = document.createElement('button');
    printBtn.className = 'ttp-rh-btn ttp-rh-btn-primary';
    printBtn.textContent = 'Print recipe';
    printBtn.addEventListener('click', () => printRecipe(recipe, logoUrl));

    actions.appendChild(saveBtn);
    actions.appendChild(printBtn);
    heroInner.appendChild(actions);

    hero.appendChild(heroInner);
    hero.appendChild(closeBtn);
    card.appendChild(hero);

    const body = document.createElement('div');
    body.className = 'ttp-rh-body';

    const grid = document.createElement('div');
    grid.className = 'ttp-rh-grid';

    if (recipe.ingredients.length) {
      const sec = document.createElement('section');
      sec.className = 'ttp-rh-section';
      _appendSectionTitle(sec, 'Ingredients');
      const ul = document.createElement('ul');
      ul.className = 'ttp-rh-ingredients';
      recipe.ingredients.forEach((item) => {
        const li = document.createElement('li');
        li.textContent = item;
        ul.appendChild(li);
      });
      sec.appendChild(ul);
      grid.appendChild(sec);
    }

    if (recipe.steps.length) {
      const sec = document.createElement('section');
      sec.className = 'ttp-rh-section';
      _appendSectionTitle(sec, 'Method');
      const ol = document.createElement('ol');
      ol.className = 'ttp-rh-steps';
      recipe.steps.forEach((step, index) => {
        const li = document.createElement('li');
        li.className = 'ttp-rh-step';
        const num = document.createElement('div');
        num.className = 'ttp-rh-step-num';
        num.textContent = String(index + 1);
        const text = document.createElement('div');
        text.className = 'ttp-rh-step-text';
        text.textContent = step;
        li.appendChild(num);
        li.appendChild(text);
        ol.appendChild(li);
      });
      sec.appendChild(ol);
      grid.appendChild(sec);
    }

    body.appendChild(grid);

    if (recipe.notes) {
      const notesWrap = document.createElement('div');
      notesWrap.className = 'ttp-rh-notes-wrap';
      const notes = document.createElement('div');
      notes.className = 'ttp-rh-notes';
      const label = document.createElement('span');
      label.className = 'ttp-rh-notes-label';
      label.textContent = "Chef's tip";
      notes.appendChild(label);
      notes.appendChild(document.createTextNode(recipe.notes));
      notesWrap.appendChild(notes);
      body.appendChild(notesWrap);
    }

    card.appendChild(body);

    return card;
  }

  function printRecipe(recipe, logoUrl = null) {
    _teardownFrame();
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(frame);
    _printFrame = frame;
    const doc = frame.contentDocument || frame.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(buildRecipeHtml(recipe, logoUrl ?? _lastLogoUrl));
    doc.close();
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
  }

  async function handleShowRecipe(msg) {
    const recipe = normalizeRecipe(msg);
    if (!recipe) return;

    console.log('[RecipeHandler] show_recipe:', recipe.title);

    if (backdrop) _teardownImmediate();

    _dismissed = false;
    _openedAsReshow = !!msg?.reshow;
    injectStyles();

    const partnerId = resolvePartnerId(widget, msg);
    _lastLogoUrl = partnerId ? await resolvePartnerLogoUrl(partnerId) : null;
    if (!_openedAsReshow) downloadRecipeHtml(recipe, _lastLogoUrl);

    backdrop = document.createElement('div');
    backdrop.className = 'ttp-rh-backdrop';
    backdrop.addEventListener('click', dismissRecipe);
    backdrop.appendChild(_buildCard(recipe, _lastLogoUrl));
    document.body.appendChild(backdrop);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => backdrop?.classList.add('ttp-rh-visible'));
    });

    backdrop._escHandler = (e) => { if (e.key === 'Escape') dismissRecipe(); };
    document.addEventListener('keydown', backdrop._escHandler);
    _minimizeWidget();

    if (!_openedAsReshow) _saveRecipe(recipe);
  }

  function _saveRecipe(recipe) {
    try {
      const store = typeof widget?.getRecipeStore === 'function' ? widget.getRecipeStore() : null;
      if (store) store.add(recipe);
    } catch (e) {
      console.warn('[RecipeHandler] Could not save recipe:', e);
    }
  }

  function _pulseStripBadge() {
    try {
      widget?._ecommerceVoiceStrip?.pulseRecipeBadge?.();
    } catch (e) { /* best-effort */ }
  }

  function dismissRecipe(opts = {}) {
    const { animate = true } = opts;
    if (_dismissed || !backdrop) return;
    _dismissed = true;
    backdrop.classList.remove('ttp-rh-visible');
    const el = backdrop;
    setTimeout(() => {
      if (el.parentNode) el.parentNode.removeChild(el);
      if (el._escHandler) document.removeEventListener('keydown', el._escHandler);
    }, 320);
    backdrop = null;
    _restoreWidget();
    if (animate && !_openedAsReshow) {
      // Slight delay so the modal fade finishes before the strip badge celebrates.
      setTimeout(() => _pulseStripBadge(), 180);
    }
    _openedAsReshow = false;
  }

  function _teardownImmediate() {
    if (backdrop) {
      if (backdrop._escHandler) document.removeEventListener('keydown', backdrop._escHandler);
      if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
      backdrop = null;
    }
    _restoreWidget();
  }

  return { handleShowRecipe, dismissRecipe, buildRecipeHtml, downloadRecipeHtml };
}
